import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReportIcon } from 'src/components/ui-icons/sidebar';
import FilterForPlanAction from 'src/pages/DashboardPage/vpo/components/planAction/filterForPlanAction/FilterForPlanAction';
import { debounce } from 'lodash';
import ChartKPI from '../chartKPI/ChartKPI';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { selectModelData } from '../../selectors/selectors';
import { fetchData } from '../../actions/actions';
import { getDepartmentsForSelectedDirectionsAndFactories, transformDataForFilters, transformDataForFiltersGOPs, transformDataForFiltersInOptimacros } from '../../utils/reports';
import { fetchKPIDataRequest, fetchKPIChartDataRequest } from '../../store/report-kpi/actions';
import { selectKPIAnswers, selectKPILoading, selectKPIChartData, selectKPIChartLoading } from '../../store/report-kpi/selectors';
import { processKPIDataByMonths, ReportAnswerKPI, transformFiltersToKPIApiFormat, getDefaultKPIData, MONTH_NAMES } from '../../utils/reportKPI';
import Loading from 'src/components/Loading';
import { selectCurrentUser } from 'src/modules/users/selector';

export const getDirectionsForSelectedGOPs = (currentFilters: any, gopDirections: any[]): any[] => {
  if (!currentFilters?.formGop?.selectedIds?.length) {
    return gopDirections;
  }
  const selectedGopIds = currentFilters.formGop.selectedIds;
  const filteredDirections = gopDirections.filter((direction) => {
    if (direction.gop && Array.isArray(direction.gop)) {
      return direction.gop.some((gopItem: any) => selectedGopIds.includes(gopItem.id));
    }
    const directionGopId = direction.gop?.id || direction.gop || direction.gopId;
    if (directionGopId) {
      return selectedGopIds.includes(directionGopId);
    }
    return false;
  });
  return filteredDirections;
};

interface FilterState {
  selected: string[];
  selectedIds: number[];
  search: string;
}

interface FilterStateDate {
  startDate: string;
  endDate: string;
}

interface CurrentFilters {
  created: FilterStateDate;
  locationKPI: FilterState;
  formGop: FilterState;
  direction?: FilterState;
  blockKPI?: FilterState;
  line?: FilterState;
  kpiName?: FilterState;
}

export const years = generateYears(2025, new Date().getFullYear());

function generateYears(fromYear: number, toYear: number) {
  const years = [];
  for (let year = fromYear; year <= toYear; year++) {
    years.push({ ID: year - fromYear + 1, Title: String(year) });
  }
  return years;
}

export const filterConfigActionGop = {
  created: {
    title: 'Период',
    hasSearch: false,
    monthYearOnly: true, 
  },
  locationKPI: {
    title: 'Локация',
    hasSearch: true,
    searchPlaceholder: 'Поиск по локации',
    options: [],
  },
  formGop: {
    title: 'GOP',
    hasSearch: true,
    searchPlaceholder: 'Поиск по GOP',
    options: [],
  },
  direction: {
    title: 'Направление',
    hasSearch: true,
    searchPlaceholder: 'Поиск по направлению',
    options: [],
  },
  blockKPI: {
    title: 'Блок',
    hasSearch: true,
    searchPlaceholder: 'Поиск по блоку',
    options: [],
  },
  line: {
    title: 'Линия/отдел',
    hasSearch: true,
    searchPlaceholder: 'Поиск по линии/отделу',
    options: [],
  },
  kpiName: {
    title: 'KPI',
    hasSearch: true,
    searchPlaceholder: 'Поиск по kpi',
    options: [],
  },
};

export const filtersOpenActionGop = {
  created: false,
  locationKPI: false,
  formGop: false,
  direction: false,
  blockKPI: false,
  line: false,
  kpiName: false,
};

const getCurrentQuarterDates = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; 

  let startMonth: number;
  let endMonth: number;

  if (currentMonth >= 1 && currentMonth <= 3) {
    // Q1
    startMonth = 1;
    endMonth = 3;
  } else if (currentMonth >= 4 && currentMonth <= 6) {
    // Q2
    startMonth = 4;
    endMonth = 6;
  } else if (currentMonth >= 7 && currentMonth <= 9) {
    // Q3
    startMonth = 7;
    endMonth = 9;
  } else {
    // Q4
    startMonth = 10;
    endMonth = 12;
  }

  return {
    startDate: `${currentYear}-${String(startMonth).padStart(2, '0')}`,
    endDate: `${currentYear}-${String(endMonth).padStart(2, '0')}`,
  };
};

const getInitialFiltersState = (): CurrentFilters => {
  const currentQuarterDates = getCurrentQuarterDates();
  return {
    created: { startDate: currentQuarterDates.startDate, endDate: currentQuarterDates.endDate },
    locationKPI: { selected: [], selectedIds: [], search: '' },
    formGop: { selected: [], selectedIds: [], search: '' },
    direction: { selected: [], selectedIds: [], search: '' },
    blockKPI: { selected: [], selectedIds: [], search: '' },
    line: { selected: [], selectedIds: [], search: '' },
    kpiName: { selected: [], selectedIds: [], search: '' },
  };
};

export default function ReportKPIMain() {
  const [currentFilters, setCurrentFilters] = useState<CurrentFilters | null>(null);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [activeDirection, setActiveDirection] = useState<number | null>(null);
  const lastFetchParams = useRef<string>('');
  const initialFiltersRef = useRef<CurrentFilters | null>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => selectCurrentUser(state));

  const locationGopKPI = useAppSelector((state: any) => selectModelData(state, 'gop-factories'));
  const formsGopKPI = useAppSelector((state: any) => selectModelData(state, 'gop-gops'));
  const directionGopKPI = useAppSelector((state: any) => selectModelData(state, 'gop-directions'));
  const blockGopKPI = useAppSelector((state: any) => selectModelData(state, 'gop-blocks'));
  const fullNameKPI = useAppSelector((state: any) => selectModelData(state, 'gop-full-name-kpis'));
  const departmentsKPI = useAppSelector((state: any) => selectModelData(state, 'gop-departments'));

  const kpiAnswers = useAppSelector(selectKPIAnswers) as ReportAnswerKPI[];
  const isLoadingKPI = useAppSelector(selectKPILoading);
  const kpiChartData = useAppSelector(selectKPIChartData);
  const isLoadingChart = useAppSelector(selectKPIChartLoading);

  const filteredDirections = useMemo(() => {
    if (!currentFilters) return [];
    return getDirectionsForSelectedGOPs(currentFilters, directionGopKPI || []);
  }, [currentFilters, directionGopKPI]);

  useEffect(() => {
    if (user?.infoVPO && locationGopKPI && locationGopKPI.length > 0 && formsGopKPI && formsGopKPI.length > 0 && !filtersInitialized) {
      const currentQuarterDates = getCurrentQuarterDates();
      let defaultLocation: { id: number; title: string } | null = null;

      const userFactoryIds = Array.isArray(user.infoVPO.factory)
        ? user.infoVPO.factory.map((f: { factoryID: number }) => f.factoryID)
        : user.infoVPO.factory?.factoryID
        ? [user.infoVPO.factory.factoryID]
        : [];

      const availableLocations = locationGopKPI.filter((location: any) => userFactoryIds.includes(location.id));

      if (availableLocations.length > 0) {
        const sortedLocations = [...availableLocations].sort((a, b) => a.title.localeCompare(b.title, 'ru'));
        defaultLocation = sortedLocations[0];
      }

      const sortedGops = [...formsGopKPI].sort((a, b) => {
        const titleA = (a.description || '').toLowerCase();
        const titleB = (b.description || '').toLowerCase();
        return titleA.localeCompare(titleB);
      });
      const firstGop = sortedGops[0];

      const newInitialFilters: CurrentFilters = {
        created: { startDate: currentQuarterDates.startDate, endDate: currentQuarterDates.endDate },
        locationKPI: defaultLocation
          ? { selected: [defaultLocation.title], selectedIds: [defaultLocation.id], search: '' }
          : { selected: ['Владивосток'], selectedIds: [10], search: '' },
        formGop: firstGop
          ? { selected: [firstGop.description], selectedIds: [firstGop.id], search: '' }
          : { selected: [], selectedIds: [], search: '' },
        direction: { selected: [], selectedIds: [], search: '' },
        blockKPI: { selected: [], selectedIds: [], search: '' },
        line: { selected: [], selectedIds: [], search: '' },
        kpiName: { selected: [], selectedIds: [], search: '' },
      };

      initialFiltersRef.current = { ...newInitialFilters };
      setCurrentFilters({ ...newInitialFilters });
      setFiltersInitialized(true);
    }
  }, [user?.infoVPO, locationGopKPI, formsGopKPI, filtersInitialized]);

  useEffect(() => {
    if (filteredDirections && filteredDirections.length > 0) {
      const isActiveDirectionValid = filteredDirections.some((dir) => dir.id === activeDirection);
      if (!isActiveDirectionValid) {
        setActiveDirection(filteredDirections[0].id);
      }
    } else if (filteredDirections && filteredDirections.length === 0 && activeDirection !== null) {
      setActiveDirection(null);
    }
  }, [filteredDirections, activeDirection]);

  const fetchKPIData = useCallback(() => {
    if (!currentFilters || (!currentFilters?.direction?.selectedIds?.length && activeDirection === null)) {
      return;
    }

    const focusGop = [
      { ID: 1, Title: 'Да', Value: true },
      { ID: 2, Title: 'Нет', Value: false },
    ];

    const filtersWithoutKpi = { ...currentFilters, kpiName: undefined };
    const apiFilters = transformFiltersToKPIApiFormat(filtersWithoutKpi, activeDirection, focusGop);
    const fetchParamsKey = JSON.stringify(apiFilters);

    if (lastFetchParams.current === fetchParamsKey) {
      return;
    }

    lastFetchParams.current = fetchParamsKey;
    dispatch(fetchKPIDataRequest(apiFilters));
  }, [currentFilters, activeDirection, dispatch]);

  useEffect(() => {
    fetchKPIData();
  }, [fetchKPIData]);

  useEffect(() => {
    if (!currentFilters) {
      return;
    }

    const focusGop = [
      { ID: 1, Title: 'Да', Value: true },
      { ID: 2, Title: 'Нет', Value: false },
    ];

    const chartFilters = transformFiltersToKPIApiFormat(currentFilters, activeDirection, focusGop);
    dispatch(fetchKPIChartDataRequest(chartFilters));
  }, [dispatch, currentFilters, activeDirection]);

  const { chartLabels, yearBoundaries } = useMemo(() => {
    const startDate = currentFilters?.created?.startDate;
    const endDate = currentFilters?.created?.endDate;
    if (!startDate || !endDate) {
      return { chartLabels: MONTH_NAMES, yearBoundaries: [] as Array<{ year: number; startIndex: number }> };
    }
    const startYear = parseInt(startDate.split('-')[0]);
    const endYear = parseInt(endDate.split('-')[0]);
    const chartLabels: string[] = [];
    const yearBoundaries: Array<{ year: number; startIndex: number }> = [];
    for (let y = startYear; y <= endYear; y++) {
      yearBoundaries.push({ year: y, startIndex: chartLabels.length });
      MONTH_NAMES.forEach((name) => chartLabels.push(name));
    }
    return { chartLabels, yearBoundaries };
  }, [currentFilters?.created?.startDate, currentFilters?.created?.endDate]);

  const kpiData = useMemo(() => {
    if (kpiAnswers && kpiAnswers.length > 0) {
      return processKPIDataByMonths(kpiAnswers, currentFilters?.created);
    }
    return getDefaultKPIData(currentFilters?.created);
  }, [kpiAnswers, currentFilters?.created]);

  useEffect(() => {
    dispatch(
      fetchData(
        'gop-factories',
        {},
        {
          page: 1,
          pageSize: 100,
        },
      ),
    );
  }, [dispatch]);

  useEffect(() => {
    if (currentFilters?.locationKPI?.selectedIds && currentFilters.locationKPI.selectedIds.length > 0) {
      dispatch(
        fetchData(
          'gop-full-name-kpis',
          { gop_factories: currentFilters.locationKPI.selectedIds },
          {
            page: 1,
            pageSize: 100,
          },
        ),
      );
    }
  }, [dispatch, currentFilters?.locationKPI?.selectedIds]);

  //! получаю направления по форме гоп
  useEffect(() => {
    const selectedGopIds = currentFilters?.formGop?.selectedIds;
    if (selectedGopIds) {
      dispatch(fetchData('gop-directions', { gop: selectedGopIds }));
    }
  }, [dispatch, currentFilters?.formGop?.selectedIds]);

  //! получаю блоки по выбранному направлению
  useEffect(() => {
    const selectedDirectionIds = currentFilters?.direction?.selectedIds;
    if (selectedDirectionIds && selectedDirectionIds.length > 0) {
      dispatch(fetchData('gop-blocks', { direction: selectedDirectionIds }));
    }
    else if (activeDirection) {
      dispatch(fetchData('gop-blocks', { direction: [activeDirection] }));
    }
    else if (filteredDirections && filteredDirections.length > 0) {
      const directionIds = filteredDirections.map((d) => d.id);
      dispatch(fetchData('gop-blocks', { direction: directionIds }));
    }
  }, [dispatch, currentFilters?.direction?.selectedIds, activeDirection, filteredDirections]);

  //! Получаю отделы на основе направлений и локаций
  useEffect(() => {
    const selectedDirectionIds = currentFilters?.direction?.selectedIds;
    const selectedLocationIds = currentFilters?.locationKPI?.selectedIds;

    let directionsToUse: number[] = [];

    if (selectedDirectionIds && selectedDirectionIds.length > 0) {
      directionsToUse = selectedDirectionIds;
    }
    else if (activeDirection) {
      directionsToUse = [activeDirection];
    }
    else if (filteredDirections && filteredDirections.length > 0) {
      directionsToUse = filteredDirections.map((d) => d.id);
    }
    if (directionsToUse.length > 0 || (selectedLocationIds && selectedLocationIds.length > 0)) {
      dispatch(
        fetchData('gop-departments', {
          ...(directionsToUse.length > 0 ? { directions: directionsToUse } : {}),
          ...(selectedLocationIds && selectedLocationIds.length > 0 ? { factories: selectedLocationIds } : {}),
        }),
      );
    } else {
      dispatch(fetchData('gop-departments'));
    }
  }, [dispatch, currentFilters?.direction?.selectedIds, currentFilters?.locationKPI?.selectedIds, activeDirection, filteredDirections]);

  //! Получаю GOP на основе групп из выбранных локаций
  useEffect(() => {
    const selectedLocationIds = currentFilters?.locationKPI?.selectedIds;
    let groupIdsFromSelectedLocations: number[] = [];
    if (selectedLocationIds && selectedLocationIds.length > 0 && locationGopKPI && locationGopKPI.length > 0) {
      locationGopKPI.forEach((locationKPI: any) => {
        if (selectedLocationIds.includes(locationKPI.id) && locationKPI.group) {
          if (Array.isArray(locationKPI.group)) {
            locationKPI.group.forEach((g: any) => {
              if (!groupIdsFromSelectedLocations.includes(g.id)) {
                groupIdsFromSelectedLocations.push(g.id);
              }
            });
          } else if (locationKPI.group.id && !groupIdsFromSelectedLocations.includes(locationKPI.group.id)) {
            groupIdsFromSelectedLocations.push(locationKPI.group.id);
          }
        }
      });
    }
    dispatch(
      fetchData(
        'gop-gops',
        {
          group: groupIdsFromSelectedLocations.length > 0 ? groupIdsFromSelectedLocations : undefined,
        },
        {
          page: 1,
          pageSize: 100,
        },
      ),
    );
  }, [dispatch, currentFilters?.locationKPI?.selectedIds, locationGopKPI]);

  const debouncedSetFilters = useMemo(
    () =>
      debounce((newFilters: any) => {
        setCurrentFilters(newFilters);
      }, 1000),
    [],
  );

  const handleFiltersChange = useCallback(
    (newFilters: any) => {
      console.log('Новые фильтры:', newFilters);
      //! Сброс фильтра kpiName при смене локации
      if (currentFilters?.locationKPI?.selectedIds?.[0] !== undefined &&
          newFilters?.locationKPI?.selectedIds?.[0] !== currentFilters?.locationKPI?.selectedIds?.[0]) {
        newFilters = { ...newFilters, kpiName: { selected: [], selectedIds: [], search: '' } };
      }
      debouncedSetFilters(newFilters);
    },
    [debouncedSetFilters, currentFilters?.locationKPI?.selectedIds?.[0]],
  );

  const filteredDepartments = useMemo(() => {
    if (!currentFilters) return [];

    let directionsToUse = currentFilters.direction?.selectedIds;

    if ((!directionsToUse || directionsToUse.length === 0) && activeDirection) {
      directionsToUse = [activeDirection];
    }

    const adaptedFilters = {
      ...currentFilters,
      factoryGop: currentFilters.locationKPI, 
      direction: {
        ...currentFilters.direction,
        selectedIds: directionsToUse,
      },
    };

    return getDepartmentsForSelectedDirectionsAndFactories(adaptedFilters, filteredDirections, locationGopKPI, departmentsKPI);
  }, [currentFilters, filteredDirections, locationGopKPI, departmentsKPI, activeDirection]);

  const transformedFactoriesGop = transformDataForFiltersInOptimacros(locationGopKPI);
  const transformedFormsGop = transformDataForFiltersGOPs(formsGopKPI);
  const transformedDirectionGop = transformDataForFiltersGOPs(filteredDirections);
  const transformedBlockGop = transformDataForFiltersGOPs(blockGopKPI);
  const transformedFullNameKPIGop = transformDataForFilters(fullNameKPI);
  const transformeddepartmentsGop = transformDataForFilters(filteredDepartments);

  const filterConfigs = useMemo(
    () => ({
      created: { items: years },
      locationKPI: { items: transformedFactoriesGop },
      formGop: { items: transformedFormsGop },
      direction: { items: transformedDirectionGop },
      blockKPI: { items: transformedBlockGop },
      line: { items: transformeddepartmentsGop },
      kpiName: { items: transformedFullNameKPIGop },
    }),
    [transformedFactoriesGop, transformedFormsGop, transformedDirectionGop, transformedBlockGop, transformedFullNameKPIGop, transformeddepartmentsGop],
  );

  const filtersMap = useMemo(
    () => ({
      created: years,
      locationKPI: transformedFactoriesGop,
      formGop: transformedFormsGop,
      direction: transformedDirectionGop,
      blockKPI: transformedBlockGop,
      line: transformeddepartmentsGop,
      kpiName: transformedFullNameKPIGop,
    }),
    [transformedFactoriesGop, transformedFormsGop, transformedDirectionGop, transformedBlockGop, transformedFullNameKPIGop, transformeddepartmentsGop],
  );

  return (
    <div className='flex justify-between w-full h-full'>
      <div className='w-[320px] mr-2 bg-white'>
        <FilterForPlanAction
          key={`filter-${currentFilters?.locationKPI?.selectedIds?.[0] || 'default'}`}
          onFiltersChange={handleFiltersChange}
          filterConfigs={filterConfigs}
          filtersMap={filtersMap}
          initialFilters={currentFilters || getInitialFiltersState()}
          filtersOpen={filtersOpenActionGop}
          filtersConfig={filterConfigActionGop}
          responsibleFil={false}
          selectedGropu={true}
          heightFilter={'max-h-screen'}
        />
      </div>

      <div className='flex-1 min-w-0 m-6'>
        <div className='flex items-center text-gray-600 mb-6'>
          <span className='pr-3'>
            <ReportIcon />
          </span>
          <span className='cursor-pointer text-base' onClick={() => navigate('/dashboard/2/reports')}>
            Отчеты
          </span>
          <span className='mx-2'>→</span>
          <span className='text-base'>Отчет по KPI</span>
        </div>

        <div className='min-w-0'>
          <div className='rounded-lg bg-white p-6 shadow-md'>
            {(isLoadingKPI || isLoadingChart) && (
              <div className='flex items-center justify-center h-64'>
                <Loading />
              </div>
            )}
            {!isLoadingKPI && !isLoadingChart && (
              <div className='rounded-lg p-4'>
                <ChartKPI data={kpiData.data} kpiPlan={kpiChartData.kpiPlan} kpiFact={kpiChartData.kpiFact} labels={chartLabels} yearBoundaries={yearBoundaries} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
